import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    Paper,
    Divider
} from '@mui/material';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SecurityIcon from '@mui/icons-material/Security';
import ExploreIcon from '@mui/icons-material/Explore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

// Import individual workflow components
import CustomClaimsWorkflow from './workflows/CustomClaimsWorkflow';
import ApiExposureWorkflow from './workflows/ApiExposureWorkflow';
import GraphExplorerWorkflow from './workflows/GraphExplorerWorkflow';

const WorkflowsTab: React.FC = () => {
    const workflows = [
        {
            id: 'workflow-custom-claims',
            title: 'Custom Claims from Extensions',
            description: 'Configure your apps to include Entra Connect directory extensions as custom claims in tokens.',
            icon: <ContentCopyIcon />,
            level: 'Advanced',
            color: 'primary'
        },
        {
            id: 'workflow-expose-api',
            title: 'Exposing & Consuming APIs',
            description: 'Learn how to expose APIs from one application and configure other applications to consume them.',
            icon: <SecurityIcon />,
            level: 'Intermediate',
            color: 'primary'
        },
        {
            id: 'workflow-graph-explorer',
            title: 'App Testing with Graph Explorer',
            description: 'Learn how to use Graph Explorer alongside App Tester to retrieve additional application data.',
            icon: <ExploreIcon />,
            level: 'Intermediate',
            color: 'primary'
        }
    ];

    return (
        <Box id="workflow-navigation">
            {/* Introduction Section - Similar to TestScenariosTab */}
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'primary.lighter', borderLeft: '5px solid', borderColor: 'primary.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SyncAltIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'primary.main' }} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                        Common Workflows
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Comprehensive <strong>step-by-step workflows</strong> guide you through common Microsoft Entra ID 
                    administration scenarios using AppConfig²'s integrated tools and interfaces.
                </Typography>
                <Typography variant="body2" color="text.primary">
                    Each workflow provides end-to-end guidance with detailed instructions, configuration examples, 
                    screenshots, and verification steps. These workflows are designed for administrators and developers 
                    implementing real-world identity scenarios.
                </Typography>
            </Paper>

            {/* Available Workflows Section */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 3, borderBottom: '2px solid', borderColor: 'primary.light', pb: 1 }}>
                Available Workflows
            </Typography>

            {/* Workflow navigation cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {workflows.map((workflow) => (
                    <Grid item xs={12} md={4} key={workflow.id}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 2,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: `${workflow.color}.main`,
                                boxShadow: `0 4px 10px rgba(25, 118, 210, 0.1)`,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 8px 20px rgba(25, 118, 210, 0.15)`,
                                    borderColor: `${workflow.color}.dark`
                                }
                            }}
                            onClick={() => document.getElementById(workflow.id)?.scrollIntoView({ behavior: 'smooth' })}
                        >
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    {React.cloneElement(workflow.icon, { 
                                        sx: { mr: 1.5, color: `${workflow.color}.main`, fontSize: '1.5rem' } 
                                    })}
                                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                        {workflow.title}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" paragraph sx={{ flexGrow: 1 }}>
                                    {workflow.description}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                    <Chip
                                        label={workflow.level}
                                        size="small"
                                        color={
                                            workflow.level === 'Beginner' ? 'success' :
                                            workflow.level === 'Intermediate' ? 'warning' : 'error'
                                        }
                                        variant="outlined"
                                    />
                                    <Chip
                                        label="View Steps"
                                        color="primary"
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}

                {/* Future Workflows Placeholder */}
                <Grid item xs={12} md={4}>
                    <Card sx={{
                        height: '100%',
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'text.disabled',
                        boxShadow: 'none',
                        bgcolor: 'background.paper'
                    }}>
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <SyncAltIcon sx={{ mr: 1.5, color: 'text.disabled', fontSize: '1.5rem' }} />
                                <Typography variant="h6" color="text.disabled" sx={{ fontWeight: 500 }}>
                                    More Workflows
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.disabled" paragraph sx={{ flexGrow: 1 }}>
                                Additional workflows for app roles, conditional access, and other advanced scenarios 
                                are being developed and will be available soon.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>
                                <Chip
                                    label="Coming Soon"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600, color: 'text.disabled', borderColor: 'text.disabled' }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Workflow Features Overview */}
            <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: '#f8f9ff', borderLeft: '5px solid', borderColor: 'info.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PlayArrowIcon sx={{ fontSize: '1.5rem', mr: 1, color: 'info.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'info.dark' }}>
                        What Each Workflow Includes
                    </Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ✅ <strong>Prerequisites checklist</strong> - Required permissions and setup
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ✅ <strong>Step-by-step instructions</strong> - Detailed guidance with screenshots
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ✅ <strong>Configuration examples</strong> - Real-world JSON, PowerShell, and UI examples
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ✅ <strong>Testing and verification</strong> - How to validate your configuration works
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ✅ <strong>Troubleshooting tips</strong> - Common issues and their solutions
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ✅ <strong>Best practices</strong> - Enterprise recommendations and security considerations
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Divider sx={{ my: 4, borderColor: 'primary.light' }} />

            {/* Include the individual workflow components */}
            <CustomClaimsWorkflow />
            <ApiExposureWorkflow />
            <GraphExplorerWorkflow />

            {/* Information Note */}
            <Paper elevation={0} sx={{ p: 3, mt: 4, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <InfoOutlinedIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="info.dark" sx={{ fontWeight: 500 }}>
                        Getting the Most from Workflows
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    These workflows are designed to be comprehensive guides that you can follow step-by-step. 
                    Each workflow builds upon fundamental Microsoft Entra ID concepts and uses AppConfig²'s tools 
                    to simplify complex configuration tasks. For best results, complete workflows in a test environment 
                    before implementing in production.
                </Typography>
            </Paper>
        </Box>
    );
};

export default WorkflowsTab;